'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { handleProgramaticScroll } from '@/_lib/utils'

import { ILink, useHeaderNavigation } from '../../_hooks'

/** Delay before closing on mouse leave — short gap between label and panel without closing too fast */
const BRANCH_CLOSE_DELAY_MS = 220

export default function AHeaderDesktopLinks({
	links,
	onLinkClick,
	className
}: {
	links: ILink[]
	onLinkClick: () => void
	className?: string
}) {
	const { isLinkActive, isParentLinkActive } = useHeaderNavigation()
	const pathname = usePathname()
	const [openKey, setOpenKey] = useState<string | null>(null)
	const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const clearLeaveTimer = useCallback(() => {
		if (leaveTimerRef.current) {
			clearTimeout(leaveTimerRef.current)
			leaveTimerRef.current = null
		}
	}, [])

	useEffect(() => {
		setOpenKey(null)
		clearLeaveTimer()
		return clearLeaveTimer
	}, [pathname, clearLeaveTimer])

	const openBranch = (key: string) => {
		clearLeaveTimer()
		setOpenKey(key)
	}

	const scheduleCloseBranch = () => {
		clearLeaveTimer()
		leaveTimerRef.current = setTimeout(() => {
			setOpenKey(null)
			leaveTimerRef.current = null
		}, BRANCH_CLOSE_DELAY_MS)
	}

	const closeDropdown = () => {
		clearLeaveTimer()
		setOpenKey(null)
		onLinkClick()
	}

	return (
		<div className={clsx('header__desktop-links', className)}>
			<ul>
				{links.map(link => {
					const hasBranch = !!link.children?.length
					const key = `${link.to}::${link.label}`
					const isOpen = hasBranch && openKey === key

					return (
						<li
							key={`link-${link.label}-${link.to}`}
							className={clsx({
								active:
									isParentLinkActive(link.to, !!link.children, link.children) &&
									link.highlight !== false,
								bold: link.bold,
								'header__desktop-links__item--open': isOpen
							})}
							onMouseEnter={hasBranch ? () => openBranch(key) : undefined}
							onMouseLeave={hasBranch ? scheduleCloseBranch : undefined}
						>
							<Link
								href={nextLinkHrefFromPathWithOptionalHash(link.to)}
								onClick={closeDropdown}
								prefetch={link.prefetch ?? false}
								title={link.label}
							>
								<span>{link.label}</span>
								<svg className='sico'>
									<use href='/assets/layout/icons.svg#s_ar1_d' />
								</svg>
							</Link>

							{link.children && (
								<ul onMouseEnter={() => openBranch(key)}>
									{link.children.map(child => (
										<li
											key={`child-${child.label}-${child.to}`}
											className={clsx({
												active:
													isLinkActive(child) && link.highlight !== false
											})}
										>
											<Link
												href={nextLinkHrefFromPathWithOptionalHash(
													child.to
												)}
												onClick={e => {
													handleProgramaticScroll(e)
													closeDropdown()
												}}
												prefetch={child.prefetch ?? false}
												title={child.label}
											>
												{child.label}
												{child.children && (
													<svg className='sico'>
														<use href='/assets/layout/icons.svg#s_ar1_d' />
													</svg>
												)}
											</Link>

											{child.children && (
												<ul>
													{child.children.map(grandchild => (
														<li
															key={`grandchild-${grandchild.label}-${grandchild.to}`}
															className={clsx({
																active: isLinkActive(grandchild)
															})}
														>
															<Link
																href={nextLinkHrefFromPathWithOptionalHash(
																	grandchild.to
																)}
																onClick={e => {
																	handleProgramaticScroll(e)
																	closeDropdown()
																}}
																prefetch={
																	grandchild.prefetch ?? false
																}
																title={grandchild.label}
															>
																{grandchild.label}
															</Link>
														</li>
													))}
												</ul>
											)}
										</li>
									))}
								</ul>
							)}
						</li>
					)
				})}
			</ul>
		</div>
	)
}
